import { BusinessObjectBase } from './base';
import type { ServiceTaskHistory } from './service-task-history';
import type { Token } from './token';

export interface ServiceTaskData {
  readonly id: string;
  readonly referenceNumber?: number;
  readonly name?: string;
  readonly queue?: string;
  readonly status?: string;
  readonly attempts?: number;
  readonly claimedBy?: string;
  readonly claimedAt?: Date;
  readonly errorMessage?: string;
  readonly data?: any;
  readonly completedAt?: Date;
  readonly tokenId: number;
  readonly token?: Token;
  readonly tokens?: Token[];
  readonly serviceTaskHistories?: ServiceTaskHistory[];
}

export interface ServiceTask extends Readonly<ServiceTaskData> {}
export class ServiceTask extends BusinessObjectBase {
  static readonly entityName = 'ServiceTask' as const;
  static readonly plural = 'service-tasks';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ServiceTask[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ServiceTask(data));
  }

  static async findOne(filter?: any): Promise<ServiceTask | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ServiceTask(data) : null;
  }

  static async findById(id: string, filter?: any): Promise<ServiceTask> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ServiceTask(data);
  }

  static async create(data: Omit<ServiceTaskData, 'id'>): Promise<ServiceTask> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ServiceTask(result);
  }

  static async updateById(
    id: string,
    data: Partial<ServiceTaskData>,
  ): Promise<ServiceTask> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new ServiceTask(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async fail(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ServiceTask.plural}/${(this as any).id}/fail`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async error(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ServiceTask.plural}/${(this as any).id}/error`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async complete(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ServiceTask.plural}/${(this as any).id}/complete`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async claim(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ServiceTask.plural}/${(this as any).id}/claim`;
    return BusinessObjectBase.post<any>(url, options);
  }
  static async claimNext(options: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/claim-next`;
    return this.post<any>(url, options);
  }
}
